import { Fragment } from 'react'
import { moeda } from '../../formato'
import type { Conferencia, LinhaMeta } from '../../conferenciaMetas'

/**
 * Aba Metas — tudo em REAIS, nenhum número em percentual.
 *
 * A diferença entre a soma das marcas e a meta geral fica à vista e **não
 * bloqueia o salvamento** (P-10). A borda amarela no campo da marca é o mesmo
 * aviso, por marca: a soma dos subitens dela não fecha com a meta dela.
 */

type Props = {
  ano: number
  metaGeral: string
  linhas: LinhaMeta[]
  conferencia: Conferencia
  aoAlterarMetaGeral: (texto: string) => void
  aoAlterarMeta: (chave: string, texto: string) => void
  aoAlterarMetaSubitem: (chave: string, codigo: string, texto: string) => void
  aoSalvar: () => void
  salvando: boolean
  salvo: boolean
  erro: string | null
  avisos: string[]
}

/** Sinal e cor da diferença: verde quando fecha, amarelo quando passa de 2 %. */
function corDaDiferenca(diferenca: number, metaGeral: number): string {
  if (diferenca === 0) return 'var(--meta)'
  return Math.abs(diferenca) / (metaGeral || 1) > 0.02 ? 'var(--atencao)' : 'var(--texto-2)'
}

export function AbaMetas({
  ano, metaGeral, linhas, conferencia,
  aoAlterarMetaGeral, aoAlterarMeta, aoAlterarMetaSubitem, aoSalvar,
  salvando, salvo, erro, avisos,
}: Props) {
  /* Sem nenhuma meta lida não existe diferença a mostrar: "−R$ 100 mi" ao lado
     de uma soma "—" seria conta feita sobre número que ninguém cadastrou. */
  const diferenca = conferencia.qtdComMeta === 0 ? null : conferencia.diferenca

  return (
    <section>
      <h2>Metas de {ano}</h2>
      <p className="ajuda">
        Valores em reais, do que será faturado em {ano}. Pedido de coleção 2027 que fature
        em {ano} conta aqui; pedido que fature em 2027, não. A soma das marcas é conferida
        contra a meta geral e a diferença fica à vista — mas o salvamento não é bloqueado
        por ela. Campo deixado em branco não altera a meta que já está gravada.
      </p>

      <div className="painel-admin">
        <table>
          <thead>
            <tr>
              <th>Marca / subitem</th>
              <th className="num coluna-meta">Meta de {ano} (R$)</th>
              <th className="num coluna-soma">Soma dos subitens</th>
            </tr>
          </thead>
          <tbody>
            {linhas.length === 0 ? (
              <tr>
                <td colSpan={3} className="ajuda">
                  Nenhuma marca carregada — sem o cadastro não há meta para editar.
                </td>
              </tr>
            ) : null}
            {linhas.map(linha => {
              const conferida = conferencia.porDivisao[linha.chave]
              const divergente = conferida?.divergente ?? false
              /* `Fragment` com chave, não array solto: array dentro de array o
                 React chaveia pela POSIÇÃO, e descartar uma marca remontaria
                 todas as linhas seguintes — o campo em edição perde o foco. */
              return (
                <Fragment key={linha.chave}>
                  <tr>
                    <td className="celula-divisao">
                      {linha.nome || '(marca nova)'}
                      {linha.ativa ? '' : ' · desativada'}
                    </td>
                    <td className="num">
                      <input
                        type="number" min={0} step={1000} value={linha.meta}
                        placeholder="—"
                        style={divergente ? { borderColor: 'var(--atencao)' } : undefined}
                        title={divergente && conferida
                          ? `Soma dos subitens: ${moeda(conferida.somaSubitens ?? 0)}`
                          : ''}
                        onChange={evento => aoAlterarMeta(linha.chave, evento.target.value)}
                      />
                    </td>
                    <td className="num">
                      {conferida === undefined || conferida.somaSubitens === null
                        ? '—'
                        : moeda(conferida.somaSubitens)}
                    </td>
                  </tr>
                  {linha.subitens.map(subitem => (
                    <tr key={subitem.codigo}>
                      <td className="celula-subitem">
                        {subitem.rotulo}
                        {subitem.novo ? ' · salve a aba Marcas antes' : ''}
                      </td>
                      <td className="num">
                        <input
                          type="number" min={0} step={1000} value={subitem.meta}
                          placeholder="—"
                          onChange={evento => aoAlterarMetaSubitem(linha.chave, subitem.codigo, evento.target.value)}
                        />
                      </td>
                      <td className="num celula-subitem">—</td>
                    </tr>
                  ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>

        <div className="conferencia">
          <div>
            <div className="linha">
              Meta geral cadastrada: <b>{conferencia.metaGeral === null ? '—' : moeda(conferencia.metaGeral)}</b>
            </div>
            <div className="linha">
              Soma das {conferencia.qtdDivisoes} marcas ativas:{' '}
              {/* Nenhuma meta lida é "ninguém cadastrou", não "a soma é zero" —
                  a mesma regra do painel: nunca mostrar zero por falha. */}
              <b>{conferencia.qtdComMeta === 0 ? '—' : moeda(conferencia.somaDivisoes)}</b>
            </div>
          </div>
          <div className="conferencia-direita">
            <div className="linha">Diferença</div>
            <div
              className="diferenca"
              style={diferenca === null || conferencia.metaGeral === null
                ? undefined
                : { color: corDaDiferenca(diferenca, conferencia.metaGeral) }}
            >
              {diferenca === null
                ? '—'
                : (diferenca === 0 ? '' : diferenca > 0 ? '+' : '−') + moeda(Math.abs(diferenca))}
            </div>
          </div>
        </div>

        <div className="rodape-acoes">
          <label className="rotulo-campo" htmlFor="meta-geral">Meta geral (R$)</label>
          <input
            id="meta-geral" className="meta-geral" type="number" min={0} step={1000}
            value={metaGeral}
            onChange={evento => aoAlterarMetaGeral(evento.target.value)}
          />
          <button className="botao primario" disabled={salvando} onClick={aoSalvar}>
            {salvando ? 'Salvando…' : 'Salvar metas'}
          </button>
          <span className={salvo ? 'salvo ver' : 'salvo'}>Metas salvas.</span>
          {erro ? <span className="erro">{erro}</span> : null}
        </div>
        {avisos.map(aviso => <div className="ajuda" key={aviso}>{aviso}</div>)}
      </div>
    </section>
  )
}
